import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@v2/components/ui/tabs";
import { seasonReferenceNow, useSeason } from "../../lib/seasons";
import { useTeamCompetitions } from "../../lib/team-config";
import { SeasonScopeToggle } from "../../components/shell/SeasonSwitcher";
import { PlayersStatsTab } from "./PlayersStatsTab";
import { TeamStatsTab } from "./TeamStatsTab";

export function StatsView({ teamId, onOpenPlayer }: { teamId: string; onOpenPlayer: (playerId: string) => void }) {
  const { t } = useTranslation("stats");
  const { selectedSeason } = useSeason();
  const [allSeasons, setAllSeasons] = useState(false);
  const [competitionId, setCompetitionId] = useState<string>("");
  const competitionsQuery = useTeamCompetitions(teamId);
  const competitions = competitionsQuery.data || [];
  const selectedCompetition = competitions.find((c) => c.id === competitionId) || null;
  const referenceNow = !allSeasons && selectedSeason?.status === "closed" ? seasonReferenceNow(selectedSeason) : undefined;
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {competitions.length > 0 && (
            <select
              value={competitionId}
              onChange={(e) => setCompetitionId(e.target.value)}
              className="h-10 rounded-full bg-raised px-4 text-sm max-w-[220px]"
              aria-label={t("filters.competition")}
            >
              <option value="">{t("filters.competitionAll")}</option>
              {competitions.map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.is_active ? "" : " ·"}</option>
              ))}
            </select>
          )}
          {selectedSeason && (
            <SeasonScopeToggle
              seasonName={selectedSeason.name}
              allSeasons={allSeasons}
              onChange={setAllSeasons}
            />
          )}
        </div>
      </div>
      {selectedCompetition && (
        <p className="text-[12px] text-muted-foreground -mt-3">
          {t("filters.competitionNote", { name: selectedCompetition.name })}
        </p>
      )}
      <Tabs defaultValue="players">
        <TabsList className="grid grid-cols-2 w-full sm:inline-flex sm:w-auto">
          <TabsTrigger value="players" className="rounded-full">{t("tabs.players")}</TabsTrigger>
          <TabsTrigger value="team" className="rounded-full">{t("tabs.team")}</TabsTrigger>
        </TabsList>
        <TabsContent value="players" className="pt-4">
          <PlayersStatsTab teamId={teamId} onOpenPlayer={onOpenPlayer} allSeasons={allSeasons} referenceNow={referenceNow} competitionId={competitionId || null} />
        </TabsContent>
        <TabsContent value="team" className="pt-4">
          <TeamStatsTab teamId={teamId} allSeasons={allSeasons} referenceNow={referenceNow} competitionId={competitionId || null} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
